<template>
  <Modal ref="modal" :dismissible="true" :show-footer="false" @modal:opened="handleModalOpened">
    <template #activator="{ show }">
      <slot name="activator" :show="show" />
    </template>

    <template #header>
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center gap-2">
          <span>Project Activity Log</span>
          <span v-if="loading" class="text-sm text-gray-500 font-normal animate-pulse">
            Loading...
          </span>
        </div>
        <button
          :disabled="loading"
          class="px-3 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          @click="refresh"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clip-rule="evenodd"
            />
          </svg>
          Refresh
        </button>
      </div>
    </template>

    <template #content>
      <div class="min-w-[40rem] max-h-[60vh] flex flex-col">
        <div v-if="loading" class="text-center py-8 text-gray-500">Loading activity logs...</div>

        <div v-else-if="error" class="text-center py-8 text-red-500">
          {{ error }}
        </div>

        <div v-else-if="auditLogs.length === 0" class="text-center py-8 text-gray-500">
          No activity logs found for this project.
        </div>

        <div v-else class="overflow-y-auto space-y-2 px-1">
          <div
            v-for="log in auditLogs"
            :key="log.id"
            class="p-3 border rounded-lg bg-white hover:bg-gray-50 transition-colors"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <span
                    class="inline-flex items-center justify-center size-8 rounded-full bg-primary-50 text-primary-500 text-xs font-medium"
                  >
                    {{ getUserInitials(log.userId) }}
                  </span>
                  <div>
                    <span class="font-medium text-sm text-dark-neutral-500">
                      {{ getUserName(log.userId) }}
                    </span>
                    <span class="text-sm text-mid-neutral-300 mx-1">•</span>
                    <span class="text-sm text-mid-neutral-300">
                      {{ log.userRole }}
                    </span>
                  </div>
                </div>
                <div class="mt-2 ml-10">
                  <span
                    class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                    :class="getOperationBadgeClass(log.operation)"
                  >
                    {{ formatAuditOperation(log.operation) }}
                  </span>
                  <div v-if="log.reviewId" class="text-xs text-mid-neutral-300 mt-1">
                    Review ID: {{ log.reviewId }}
                  </div>
                  <div v-if="log.reviewItemId" class="text-xs text-mid-neutral-300 mt-1">
                    Item ID: {{ log.reviewItemId }}
                  </div>
                </div>
              </div>
              <div class="text-xs text-mid-neutral-300">
                {{ format(log.createdAt, dateFormat + " HH:mm") }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="!loading" class="flex items-center justify-between border-t pt-4 mt-4">
          <p class="text-sm font-medium text-neutral-text-title">Total Logs: {{ total }}</p>
          <Pagination
            v-model:page="currentPage"
            v-model:items-per-page="itemsPerPage"
            :total-items="total"
            items-label="Logs"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import Modal from "@app/components/Global/Modal/Modal.vue";
import Pagination from "@app/components/Global/Pagination/Pagination.vue";
import useAuditLogs from "./use-audit-logs";
import { User } from "@core/domain/models/user.model";
import { useSettings } from "@app/composables/use-settings";
import { format } from "date-fns";
import { formatAuditOperation } from "@app/utils/auditOperationLabels";

const props = defineProps<{
  projectId: number;
  users: User[];
}>();

const modal = ref<InstanceType<typeof Modal>>();
const itemsPerPage = ref(10);
const { dateFormat } = useSettings();

const { auditLogs, loading, error, total, currentPage, fetchAuditLogs } = useAuditLogs();

watch(currentPage, (newPage) => {
  fetchAuditLogs(props.projectId, newPage, itemsPerPage.value);
});

function handleModalOpened() {
  currentPage.value = 1;
  refresh();
}

async function refresh() {
  await fetchAuditLogs(props.projectId, currentPage.value, itemsPerPage.value);
}

function getUserName(userId: string): string {
  const user = props.users.find((u) => u.id === userId);
  if (user) {
    return `${user.firstName} ${user.lastName}`;
  }
  return "Unknown User";
}

function getUserInitials(userId: string): string {
  const user = props.users.find((u) => u.id === userId);
  if (user) {
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  }
  return "?";
}

function getOperationBadgeClass(operation: string): string {
  if (operation.includes("CREATED") || operation.includes("ADDED")) {
    return "bg-green-100 text-green-700";
  }
  if (operation.includes("DELETED") || operation.includes("REMOVED")) {
    return "bg-red-100 text-red-700";
  }
  if (operation.includes("UPDATED") || operation.includes("RENAMED")) {
    return "bg-blue-100 text-blue-700";
  }
  if (operation.includes("LOCKED")) {
    return "bg-yellow-100 text-yellow-700";
  }
  if (operation.includes("UNLOCKED")) {
    return "bg-purple-100 text-purple-700";
  }
  return "bg-gray-100 text-gray-700";
}

defineExpose({
  show: () => modal.value?.show(),
  hide: () => modal.value?.hide(),
});
</script>
